import React from "react";
import { Link } from "react-router-dom";
import { trackDonateClick } from "../lib/api";
import { shareOrigin } from "../lib/shareOrigin";
import InlineShareButton from "../components/InlineShareButton";
import {
  ArrowRight, FireSimple, MapTrifold, ShieldCheck, Warning, Path, VideoCamera,
  Mountains, Airplane, Broadcast, Lightning, Wind, Bell, Newspaper, Globe,
  ChartLineUp, Users, FilePdf, Planet as SatelliteIcon, MapPin, Heart,
  CompassRose, HouseLine, Barricade, Camera, CloudRain, PushPin, ShareNetwork,
  Fire, Drop, Waves, MoonStars, PlayCircle, DeviceMobile, GoogleLogo, Ruler, Translate,
} from "@phosphor-icons/react";

const GROUPS = [
  {
    title: "Live Fire Intelligence",
    items: [
      { icon: FireSimple, title: "Live Dispatch Feed", desc: "Real-time WildCAD-E incidents from 8 WA dispatch centers — flame icons, smoke checks, prescribed burns, county-filtered." },
      { icon: MapTrifold, title: "Tactical Dark Map", desc: "High-contrast map with pulse markers, official fire perimeters, evacuation zones, and county outlines — with an address/town search on every map." },
      { icon: SatelliteIcon, title: "Satellite Hotspots + Growth Estimates", desc: "NASA VIIRS infrared detections, plus automatic growth estimates when fresh hotspots extend beyond the mapped perimeter." },
      { icon: CompassRose, title: "Spread Direction & Progression", desc: "Satellite time-cohort analysis shows which way a fire is moving — compass arrow on the map, miles per day, and hotspots that fade with age." },

      { icon: ChartLineUp, title: "Official Acreage & Containment", desc: "NIFC records, GIS perimeter acres, WA DNR stats, and 7-day growth sparklines merged onto every incident." },
    ],
  },
  {
    title: "Evacuations & Alerts",
    items: [
      { icon: Warning, title: "Evacuation Zones + Road Closures", desc: "Level 1 / 2 / 3 zones from county Emergency Management, drawn live on the map — with the road closures inside each zone (WSDOT + County EM) one tap away." },
      { icon: HouseLine, title: "Is Your Place in the Line of Fire?", desc: "Drop a pin at your address on any fire page — see the distance, direction, and whether satellite spread is pointing your way." },
      { icon: MapPin, title: "Saved Locations with Radius Alarms", desc: "Pin your home, cabin, or trailhead and get push alerts when fire activity gets close." },
      { icon: Bell, title: "Follow Fires + Smart Push", desc: "Followed fires get pinned with live distance from your location, plus push updates, Large Airtanker proximity, Rapid Fire Growth (2x in 24 hrs), edge-approach and fire-convergence warnings, lightning clusters near your pin, and daily 8 AM briefing + 8 PM recap." },
      { icon: MoonStars, title: "Quiet Hours", desc: "Mute informational pushes overnight on every device you sign in on — Level 3 GO evacuations and official flood warnings always break through." },
      { icon: Newspaper, title: "Morning Briefings", desc: "NWCC Morning Brief (PNW preparedness levels, IMT rotation, team assignments) and the NIFC national briefing, parsed daily." },
    ],
  },
  {
    title: "Prescribed Burns & Fall Readiness",
    items: [
      { icon: Fire, title: "Rx Burns — Exact Units", desc: "Purple diamonds for active NIFC prescribed burns and WA DNR permitted burns, with USFS FACTS planned unit boundaries drawn on the map. A 'Burning Today' strip tells you which permitted burns are lit, so planned smoke never looks like an emergency." },
      { icon: Bell, title: "Burn Heads-up Pushes", desc: "When DNR approves a burn inside your saved-location radius you get a heads-up push before the column goes up." },
      { icon: Waves, title: "Flood & Debris-Flow Watch", desc: "Burn scars, live USGS stream gauges, downstream debris-flow paths, and NWS flood warnings — rain on a scar is checked against real gauge rainfall before an alert escalates." },
      { icon: Drop, title: "Debris-on-Road Reports", desc: "Verified reporters drop a one-tap 'Debris on road' pin from their GPS position so neighbors see washouts and mudflows as they happen." },
    ],
  },
  {
    title: "Aircraft & Field Resources",
    items: [
      { icon: Airplane, title: "Live Aircraft Tracking", desc: "Firefighting tankers, helicopters, and lead planes via ADS-B — with photos, callsigns, and flight trails. Free." },
      { icon: VideoCamera, title: "Wildfire Cameras", desc: "Public Pano AI / WA DNR wildfire camera network, viewable inline." },
      { icon: Broadcast, title: "Live Scanner Audio", desc: "Broadcastify dispatch radio feeds embedded for fire traffic listening." },
    ],
  },
  {
    title: "Roads, Air & Weather",
    items: [
      { icon: Path, title: "WSDOT Road Alerts", desc: "Real-time highway closures and event status for your county." },
      { icon: Mountains, title: "Mountain Pass Conditions", desc: "Live road condition and restriction data for relevant passes." },
      { icon: Wind, title: "Incident Fire Weather", desc: "Live NWS wind speed & direction, temperature, humidity, and a 6-hour wind outlook on every fire page — wind drives fire." },
      { icon: CloudRain, title: "Live Wind & Rain Radar Layers", desc: "Toggle live wind arrows from NWS stations and animated NEXRAD rain radar right on the map — watch weather move across the fire ground." },
      { icon: Lightning, title: "Lightning, AQI, Smoke & NWS Alerts", desc: "Blitzortung strike map, AirNow AQI monitors, NOAA smoke forecast polygons, Red Flag Warnings, and burn bans by county." },
    ],
  },
  {
    title: "Coverage & Community",
    items: [
      { icon: Globe, title: "Multi-Region Views", desc: "County dashboards, a Washington statewide view, and a BC Border (Canada) view with BC Wildfire Service fires and evacuations." },
      { icon: Camera, title: "Geotagged Field Photos", desc: "Verified reporters attach photos to field updates — geotagged shots drop as pins on the map so you can see exactly what's happening where." },

      { icon: Users, title: "Verified Reporters", desc: "Trained locals post gold-shield field updates straight from the ground. Apply from your profile." },
      { icon: PushPin, title: "Community Info Pins", desc: "Staff drop map pins for shelters, donation drives, and community posts — with links and auto-expiry so the map stays clean." },
      { icon: ShareNetwork, title: "Share My Status Check-ins", desc: "Generate a daily check-in card for your saved location — nearest fire, distance, and a QR code — ready to post so friends and family know you're okay." },
      { icon: GoogleLogo, title: "Sign in with Google", desc: "Join with one tap using your Google account, or classic email + password — your saved locations and quiet hours follow your account." },
    ],
  },
  {
    title: "Learn & Install",
    items: [
      { icon: PlayCircle, title: "Narrated Video Walkthroughs", desc: "A 5-minute desktop tour (smoke checks, fires, every notification) and a 2-minute phone tour, with captions, short Facebook clips, and a built-in share menu — on the Guide page." },
      { icon: Translate, title: "English + Español", desc: "Both walkthroughs have a Spanish voice track and Spanish captions; the Guide auto-selects your browser language." },
      { icon: DeviceMobile, title: "Installs Like an App", desc: "Add Firewatch to your home screen on iPhone or Android — full screen, push alerts, self-updating. First-time phone visitors get a quick welcome tour." },
      { icon: Ruler, title: "Map Screenshot & Measure", desc: "Copy a watermarked, time-stamped screenshot of your exact map view, and tape distances in miles leg by leg — ready for a post." },
    ],
  },
];

export default function About() {
  const caption = [
    "🔥 Okanogan Highlands Firewatch — free community wildfire dashboard for north-central Washington.",
    "Live dispatch, evacuations, fire perimeters, satellite hotspots, prescribed burns, flood & debris-flow watch, cameras, lightning, smoke and road closures — all in one map. Now with narrated video walkthroughs in English and Spanish.",
    `See what it can do: ${shareOrigin()}/about`,
  ].join("\n");
  return (
    <div data-testid="about-page" className="max-w-3xl mx-auto px-4 sm:px-6 py-10">
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="flex items-center gap-3">
          <div className="text-[15px] uppercase tracking-[0.18em] font-bold text-tac-faint">
            About this dashboard
          </div>
          <Link to="/whats-new" data-testid="about-whats-new-link" className="text-[11px] font-bold uppercase tracking-wider text-tac-primary hover:underline">
            What's new →
          </Link>
        </div>
        <InlineShareButton
          testid="about-share-btn"
          url={`${shareOrigin()}/about`}
          title="Okanogan Highlands Firewatch"
          text={caption}
          caption={caption}
        />
      </div>
      <h1 className="font-display font-black text-3xl sm:text-4xl tracking-tight text-tac-text mb-4">
        Built for the people who live with fire.
      </h1>
      <p className="text-[15px] sm:text-[19px] text-tac-dim leading-relaxed mb-8">
        Okanogan Highlands Firewatch is a community wildfire situational awareness tool
        for north-central Washington — Okanogan, Chelan, Ferry, Stevens, Kittitas, and
        neighboring counties, plus a statewide Washington view and the BC border region.
        It aggregates more live fire data sources in one screen than any other service —
        dispatch, aircraft, satellites, scanners, lightning, smoke, air quality, roads,
        and evacuations — all free, ad-free, and community-run.
      </p>

      {GROUPS.map((g) => (
        <section key={g.title} data-testid={`about-group-${g.title.toLowerCase().replace(/[^a-z]+/g, "-")}`} className="mb-8">
          <h2 className="text-[13px] uppercase tracking-[0.18em] font-bold text-tac-primary mb-3">
            {g.title}
          </h2>
          <div className="grid sm:grid-cols-2 gap-3">
            {g.items.map((f) => (
              <Feature key={f.title} icon={f.icon} title={f.title} desc={f.desc} />
            ))}
          </div>
        </section>
      ))}

      <div className="border border-tac-border bg-tac-surface/40 p-5 mb-6">
        <div className="flex items-start gap-3">
          <ShieldCheck size={20} weight="duotone" className="text-tac-warning shrink-0 mt-0.5" />
          <div>
            <h3 className="font-display font-bold text-tac-text mb-1">Important Notice</h3>
            <p className="text-[15px] text-tac-dim leading-relaxed">
              This is a community tool. Always follow official evacuation orders from your county
              Emergency Management, Sheriff's Office, and DNR. For life-threatening emergencies,
              call <span className="font-mono-tac text-tac-primary">911</span>. Data is sourced from
              public APIs and may lag real-world conditions.
            </p>
          </div>
        </div>
      </div>

      <div className="border border-tac-border bg-tac-surface/40 p-5 mb-6">
        <h3 className="font-display font-bold text-tac-text mb-2">Data Sources</h3>
        <ul className="text-[15px] text-tac-dim space-y-1.5 font-mono-tac">
          <li>• WildCAD-E Dispatch Centers (WACWC, WANEC, WACAC, WAPCC, WANDC, WASPC, WAOLC, WAPSC)</li>
          <li>• NIFC / WFIGS incident records &amp; interagency fire perimeters</li>
          <li>• NASA FIRMS VIIRS active fire detections</li>
          <li>• NWCC Morning Brief &amp; NIFC National Fire News</li>
          <li>• National Weather Service Active Alerts API (api.weather.gov)</li>
          <li>• WSDOT Traveler Information API (HighwayAlerts + MountainPassConditions)</li>
          <li>• WA DNR / Pano AI Wildfire Watch (wadnr.wildfirewatch.com)</li>
          <li>• WA DNR Burn Portal permitted burns · USFS FACTS treatment units · NIFC RX records</li>
          <li>• USGS stream gauges &amp; rainfall (waterservices.usgs.gov) · USGS NLDI downstream traces</li>
          <li>• WA DNR Wildfire Danger / Burn Bans · Okanogan County burn-ban notices</li>
          <li>• BC Wildfire Service &amp; EmergencyInfoBC (Canada)</li>
          <li>• ADS-B aircraft data (airplanes.live / adsb.lol) + Planespotters photos</li>
          <li>• AirNow AQI · NOAA smoke forecast · Blitzortung lightning · Broadcastify audio</li>
          <li>• Map tiles: CARTO Dark Matter / OpenStreetMap contributors</li>
        </ul>
      </div>

      <div className="border border-tac-border bg-tac-surface/40 p-5 mb-6">
        <h3 className="font-display font-bold text-tac-text mb-2">Coverage</h3>
        <p className="text-[15px] text-tac-dim leading-relaxed mb-2">
          Currently optimized for 13 WA counties along the fire-prone east slope of the Cascades
          and northern reaches of the state — plus statewide and cross-border views:
        </p>
        <p className="text-xs font-mono-tac text-tac-text">
          Okanogan · Chelan · Kittitas · Douglas · Grant · Ferry · Stevens · Pend Oreille · Spokane · Yakima · Klickitat · Whatcom · Skagit · Washington Statewide · BC Border (Canada)
        </p>
      </div>

      <div
        data-testid="about-donate-block"
        className="border border-tac-primary/50 bg-tac-primary/10 p-5 mb-6"
      >
        <div className="flex items-start gap-3">
          <Heart size={20} weight="fill" className="text-tac-primary shrink-0 mt-0.5" />
          <div className="min-w-0">
            <h3 className="font-display font-bold text-tac-text mb-1">Support Firewatch</h3>
            <p className="text-[15px] text-tac-dim leading-relaxed mb-3">
              Firewatch is community-funded and run by volunteers — no ads, no paywalls,
              free for everyone. Every dollar goes to servers and data feeds that keep
              this dashboard live through fire season.
            </p>
            <Link
              to="/donate"
              data-testid="about-donate-btn"
              onClick={() => trackDonateClick("about")}
              className="inline-flex items-center gap-1.5 px-4 py-2 bg-tac-primary text-white text-xs font-bold uppercase tracking-wider rounded-sm hover:bg-tac-primary/90 active:scale-95 transition"
            >
              <Heart size={12} weight="fill" /> Donate
            </Link>
          </div>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <Link to="/" data-testid="cta-back-to-map" className="inline-flex items-center gap-2 px-4 py-2 bg-tac-primary text-white text-xs font-bold uppercase tracking-wider rounded-sm hover:bg-tac-primary/90 active:scale-95 transition">
          Back to Live Map <ArrowRight size={14} weight="bold" />
        </Link>
        <Link
          to="/compare"
          data-testid="about-features-pdf"
          className="inline-flex items-center gap-2 px-4 py-2 border border-tac-border bg-tac-surface text-tac-text text-xs font-bold uppercase tracking-wider rounded-sm hover:bg-tac-surface2 transition"
        >
          <FilePdf size={14} weight="fill" className="text-tac-primary" /> Features &amp; Comparison
        </Link>
      </div>
    </div>
  );
}

function Feature({ icon: Icon, title, desc }) {
  return (
    <div className="border border-tac-border bg-tac-surface/40 p-4">
      <Icon size={20} weight="duotone" className="text-tac-primary mb-2" />
      <h3 className="font-display font-bold text-sm text-tac-text mb-1">{title}</h3>
      <p className="text-[15px] text-tac-dim leading-relaxed">{desc}</p>
    </div>
  );
}
